import React, { useReducer, useEffect, useState } from 'react'
import { Box, FormControlLabel, Checkbox, styled } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'
import PeopleSearch from '../PeopleSearch'
import DateTimeFilter from '../DateTimeFilter'
import Button from '../Button'
import { isNilOrEmpty, useGetControlsAccess } from 'packages/core'
import { Spinner, Tooltip } from 'packages/eid-ui'
import moment from 'moment'
import {
    useCreateCollaborationTaskDelegation,
    useJustificationOptions,
} from 'hooks'
import { styles, ItemContainer, reducers } from './utils'
import {
    mobileScreenWidth,
    ITEM_COMMENT_MAX_LENGTH,
    ITEM_COMMENT_MIN_LENGTH,
} from 'utils'
import { JustificationSelector } from 'packages/eid-ui/JustificationSelector'
import useSubcomponents from 'useSubcomponents'

const StyledFormControlLabel = styled(FormControlLabel)(({ checked }) => ({
    '& .MuiFormControlLabel-label': {
        color: checked ? '#000000' : '#767676',
    },
}))

const StyledDateFilterContainer = styled(Box)({
    [`@media (max-width:${mobileScreenWidth})`]: {
        width: '100%',
    },
})

const LabelBox = styled(Box)({
    color: '#73767A',
    fontSize: '1.2rem',
    paddingLeft: '0.8rem',
    marginBottom: '-0.4rem',
})

const NewCollaborationTaskDelegationForm = ({ onSave, onCancel }) => {
    const [state, dispatch] = useReducer(reducers, {
        people: [],
        startDate: moment().startOf('day').utc(),
        endDate: null,
        delegateForever: false,
        activeOnlyIfOutOfOffice: false,
    })

    const [justification, setJustification] = useState({
        comment: '',
        isPredefined: false,
        localKeyName: null,
    })
    const { hasAccessToControl } = useSubcomponents()

    const hasJustificationAccess = hasAccessToControl(
        'MyIdentity-PermanentDelegations-Justification-Control',
    )
    const { t } = useTranslation()

    const {
        mutateAsync: createDelegation,
        isLoading,
        isSuccess,
    } = useCreateCollaborationTaskDelegation()

    const {
        data: justificationOptions = [],
        isLoading: isLoadingJustifications,
    } = useJustificationOptions()

    const controls = useGetControlsAccess().map((c) => c.name)

    const hasOutOfOfficeAccess = controls.includes(
        'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control',
    )

    const handleCreateDelegation = () => {
        const dataToSubmit = {
            startDate: state.startDate,
            endDate: state.endDate,
            delegateePersonIds: state.people.map((p) => p.id),
        }
        if (hasOutOfOfficeAccess) {
            dataToSubmit.activeOnlyIfOutofOffice = state.activeOnlyIfOutOfOffice
        }
        if (hasJustificationAccess) {
            if (justification.isPredefined && justification.localKeyName) {
                // Send localKeyName for predefined options (for localization)
                dataToSubmit.justificationComment = justification.localKeyName
            } else if (justification.comment) {
                // Send custom comment for "Other" option
                dataToSubmit.justificationComment = justification.comment
            }
        }
        createDelegation(dataToSubmit)
    }

    useEffect(() => {
        if (isSuccess) onSave()
    }, [isSuccess, onSave])

    const endDateCheck =
        (!state.delegateForever && isNilOrEmpty(state.endDate)) ||
        state.startDate?.isAfter(state.endDate)

    const isJustificationValid = () => {
        if (!hasJustificationAccess) {
            return true // No validation if user doesn't have access
        }

        // If a predefined option is selected, it's always valid
        if (justification.isPredefined) {
            return true
        }

        // For "Other" option, validate the custom text
        const commentLength = justification.comment?.length || 0

        // Check min length
        if (commentLength < ITEM_COMMENT_MIN_LENGTH) {
            return false
        }

        // Check max length
        if (commentLength > ITEM_COMMENT_MAX_LENGTH) {
            return false
        }

        return true
    }
    return (
        <>
            <ItemContainer>
                <PeopleSearch
                    url={'/api/v1/Delegation/approvers'}
                    endAdornmentComponent={() => <></>}
                    label={t('PersonName')}
                    placeholder={t('SelectPerson')}
                    onChange={(_, value) => {
                        dispatch({
                            type: 'SET_PEOPLE',
                            payload: value,
                        })
                    }}
                    rootStyleProps={styles.peopleSearch}
                    value={state.people}
                    multiple
                />
            </ItemContainer>

            <ItemContainer display="flex" marginLeft="12px">
                <StyledFormControlLabel
                    sx={{
                        marginRight: '30px',
                    }}
                    control={
                        <Checkbox
                            checked={state.delegateForever}
                            icon={<Icon name="CheckBox" />}
                            checkedIcon={<Icon name="CheckedBox" />}
                            onChange={(e) =>
                                dispatch({
                                    type: 'SET_DELEGATE_FOREVER',
                                    payload: e.target.checked,
                                })
                            }
                        />
                    }
                    checked={state.delegateForever}
                    label={t('DelegateForever')}
                />
                {hasOutOfOfficeAccess && (
                    <StyledFormControlLabel
                        data-protectedsubcomponent={
                            'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control'
                        }
                        control={
                            <Checkbox
                                checked={state.activeOnlyIfOutOfOffice}
                                icon={<Icon name="CheckBox" />}
                                checkedIcon={<Icon name="CheckedBox" />}
                                onChange={(e) =>
                                    dispatch({
                                        type: 'SET_OUT_OF_OFFICE',
                                        payload: e.target.checked,
                                    })
                                }
                            />
                        }
                        checked={state.activeOnlyIfOutOfOffice}
                        label={t('OnlyWhenOutOfOffice')}
                    />
                )}
            </ItemContainer>

            <ItemContainer display="flex" marginLeft="-8px">
                <StyledDateFilterContainer padding="0px 8px">
                    <DateTimeFilter
                        disablePast={true}
                        margin="normal"
                        label={t('StartDate')}
                        placeholder={t('SelectDate')}
                        onChange={(date) => {
                            let dateToSet
                            if (!isNilOrEmpty(date)) {
                                dateToSet = moment(date)
                                    .clone()
                                    .startOf('day')
                                    .utc()
                            }
                            dispatch({
                                type: 'SET_START_DATE',
                                payload: dateToSet,
                            })
                        }}
                        value={
                            isNilOrEmpty(state.startDate)
                                ? null
                                : state.startDate.clone().local()
                        }
                    />
                </StyledDateFilterContainer>

                {!state.delegateForever && (
                    <Box padding="0px 8px ">
                        <DateTimeFilter
                            minDate={
                                !isNilOrEmpty(state.startDate) &&
                                state.startDate.clone().local()
                            }
                            disabled={state.delegateForever}
                            label={t('EndDate')}
                            placeholder={t('SelectDate')}
                            onChange={(date) => {
                                let dateToSet
                                if (!isNilOrEmpty(date)) {
                                    dateToSet = moment(date)
                                        .clone()
                                        .endOf('day')
                                        .subtract(1, 'minutes')
                                        .utc()
                                }
                                dispatch({
                                    type: 'SET_END_DATE',
                                    payload: dateToSet,
                                })
                            }}
                            value={
                                isNilOrEmpty(state.endDate)
                                    ? null
                                    : state.endDate.clone().local()
                            }
                        />
                    </Box>
                )}
            </ItemContainer>

            {hasJustificationAccess && (
                <ItemContainer
                    data-protectedsubcomponent={
                        'MyIdentity-PermanentDelegations-Justification-Control'
                    }
                >
                    {isLoadingJustifications ? (
                        <>
                            <Spinner size={0.8} />
                        </>
                    ) : (
                        <>
                            <LabelBox>{t('Justification')}</LabelBox>
                            <JustificationSelector
                                options={justificationOptions}
                                value={
                                    justification.isPredefined
                                        ? justification.localKeyName
                                        : justification.comment
                                }
                                onChange={(comment, isPredefined) => {
                                    // For predefined: comment is the localKeyName
                                    // For custom: comment is the actual text
                                    setJustification({
                                        comment: isPredefined ? '' : comment, // Store custom text
                                        isPredefined: isPredefined || false,
                                        localKeyName: isPredefined
                                            ? comment
                                            : null, // Store localKeyName
                                    })
                                }}
                                multiline={true}
                                maxCharacters={ITEM_COMMENT_MAX_LENGTH}
                                minCharacters={ITEM_COMMENT_MIN_LENGTH}
                                showCharacterCount={true}
                                isLoading={isLoadingJustifications}
                            />
                        </>
                    )}
                </ItemContainer>
            )}

            <ItemContainer
                display="flex"
                marginLeft="-8px"
                alignItems="center"
                flexDirection="row !important"
                flexWrap="wrap"
            >
                <Tooltip
                    title={
                        isNilOrEmpty(state.startDate) ||
                        isNilOrEmpty(state.people) ||
                        endDateCheck
                            ? t('RequiredFieldsMissing')
                            : ''
                    }
                >
                    <Box>
                        <Button.Rounded
                            loading={isLoading}
                            styles={{ minWidth: '144px !important' }}
                            disabled={
                                isNilOrEmpty(state.startDate) ||
                                isNilOrEmpty(state.people) ||
                                endDateCheck ||
                                isLoading ||
                                !isJustificationValid()
                            }
                            onClick={() => {
                                handleCreateDelegation()
                            }}
                        >
                            {t('CreateDelegation')}
                        </Button.Rounded>
                    </Box>
                </Tooltip>

                <Button.Rounded onClick={onCancel} styles={styles.cancelButton}>
                    {t('Cancel')}
                </Button.Rounded>
            </ItemContainer>
        </>
    )
}

export default NewCollaborationTaskDelegationForm
